/** @format */

import { calculateTotalCharge } from "./stripeFeeCalculator";

/**
 * Late Fee Calculator for Frontend
 *
 * This utility calculates late fees for missed contributions.
 * Late fees are charged when a contribution is paid more than 24 hours
 * after its scheduled contribution day.
 *
 * Late Fee: 2.5% of contribution amount per late date
 */

// Late fee constants (must match backend)
export const LATE_FEE_PERCENTAGE = 0.025; // 2.5%
export const LATE_FEE_GRACE_PERIOD_HOURS = 24;

/**
 * Calculate late fee for a single contribution
 * @param contributionAmount - The contribution amount in dollars
 * @returns The late fee in dollars
 */
export const calculateLateFeePerContribution = (
	contributionAmount: number,
): number => {
	const fee = contributionAmount * LATE_FEE_PERCENTAGE;
	return Math.round(fee * 100) / 100;
};

/**
 * Check if a contribution date is late (past 24hr grace period)
 * @param dateStr - Date string in YYYY-MM-DD format
 * @param timezone - User's timezone (optional, currently unused)
 * @returns true if the date is late
 */
export const isDateLate = (dateStr: string, timezone?: string): boolean => {
	void timezone;

	const now = new Date();
	const [year, month, day] = dateStr.split("-").map(Number);

	const contributionEndDate = new Date(year, month - 1, day, 23, 59, 59);
	const graceEndDate = new Date(
		contributionEndDate.getTime() +
			LATE_FEE_GRACE_PERIOD_HOURS * 60 * 60 * 1000,
	);

	return now > graceEndDate;
};

/**
 * Separate dates into late and non-late categories
 */
export const separateLateDates = (
	missedDates: string[],
	timezone?: string,
): {
	lateDates: string[];
	nonLateDates: string[];
} => {
	const lateDates: string[] = [];
	const nonLateDates: string[] = [];

	for (const dateStr of missedDates) {
		if (isDateLate(dateStr, timezone)) {
			lateDates.push(dateStr);
		} else {
			nonLateDates.push(dateStr);
		}
	}

	return { lateDates, nonLateDates };
};

/**
 * Calculate complete fee breakdown for missed contributions
 */
export const calculateMissedContributionFees = (
	contributionAmount: number,
	missedDates: string[],
	timezone?: string,
	currency: string = "usd",
): {
	contributionPerDate: number;
	totalDates: number;
	contributionsSubtotal: number;

	lateDatesCount: number;
	nonLateDatesCount: number;
	lateFeePerDate: number;
	totalLateFees: number;

	subtotalBeforeStripeFee: number;

	stripeFee: number;

	grandTotal: number;

	hasLateFees: boolean;
	lateDates: string[];
	nonLateDates: string[];
} => {
	const { lateDates, nonLateDates } = separateLateDates(missedDates, timezone);

	const totalDates = missedDates.length;
	const contributionsSubtotal =
		Math.round(contributionAmount * totalDates * 100) / 100;

	const lateFeePerDate = calculateLateFeePerContribution(contributionAmount);
	const totalLateFees =
		Math.round(lateFeePerDate * lateDates.length * 100) / 100;

	const subtotalBeforeStripeFee =
		Math.round((contributionsSubtotal + totalLateFees) * 100) / 100;

	const feeBreakdown = calculateTotalCharge(subtotalBeforeStripeFee, currency);

	return {
		contributionPerDate: contributionAmount,
		totalDates,
		contributionsSubtotal,

		lateDatesCount: lateDates.length,
		nonLateDatesCount: nonLateDates.length,
		lateFeePerDate,
		totalLateFees,

		subtotalBeforeStripeFee,

		stripeFee: feeBreakdown.stripeFee,
		grandTotal: feeBreakdown.totalCharge,

		hasLateFees: lateDates.length > 0,
		lateDates,
		nonLateDates,
	};
};

/**
 * Format a date string for display
 */
export const formatDateForDisplay = (dateStr: string): string => {
	const [year, month, day] = dateStr.split("-").map(Number);
	const date = new Date(year, month - 1, day);
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
